import capitalize from 'lodash/capitalize';
import React, { useEffect, useState } from 'react';

import SubIndustryList from '@components/OnboardingModal/ProfileFields/SubIndustryList';
import SubJobFunctionList from '@components/OnboardingModal/ProfileFields/SubJobFunctionList';
import SectionBreakTitle, { SectionColor } from '@components/SectionBreakTitle';
import Spinner from '@components/Spinner';
import { useCurrentUser, useProfile, useUserSettings } from '@hooks';
import { READ_ONLY_MODE, READ_ONLY_PHRASE } from '@src/env';
import {
  COMPANY_SIZE_OPTIONS,
  JOB_FUNCTION_OPTIONS,
  JOB_LEVEL_OPTIONS,
  ORGANIZATION_INDUSTRY_OPTIONS,
  PROFILE_OTHER_OPTION,
} from '@utils/backend-api/profileSettings';
import { FirstPartyDataProps } from '@utils/backend-api/userProfile';
import { BLANK_OPTION, BLANK_RECORD_OPTION } from '@utils/constants';

import LinkSection from './LinkSection';
import UserForm from './UserForm';

export interface InitialStateProps extends FirstPartyDataProps {
  name: string;
  email: string;
  password?: string;
  passwordConfirmation?: string;
}

const INITIAL_STATE = {
  name: '',
  email: '',
  password: '',
  passwordConfirmation: '',
  company: BLANK_RECORD_OPTION,
  companySize: BLANK_OPTION,
  customIndustry: BLANK_RECORD_OPTION,
  customJobFunction: BLANK_RECORD_OPTION,
  customJobLevel: BLANK_RECORD_OPTION,
  customSubIndustry: BLANK_RECORD_OPTION,
  customSubJobFunction: BLANK_RECORD_OPTION,
  industry: BLANK_OPTION,
  jobFunction: BLANK_OPTION,
  jobLevel: BLANK_OPTION,
  postalCode: '',
  subIndustry: BLANK_OPTION,
  subJobFunction: BLANK_OPTION,
};

const Account = () => {
  const [initialValues, setInitialValues] = useState<InitialStateProps>(INITIAL_STATE);
  const { userSettings, isLoadingUserSettings } = useUserSettings();
  const { profile, isLoadingProfile } = useProfile();
  const { currentUser } = useCurrentUser();

  useEffect(() => {
    const getSelectedOptions = (possibleOptions, profileKey) => {
      const selectedStandardValue = profile?.[profileKey];
      const standardOption =
        possibleOptions?.find((item) => item.value === selectedStandardValue) || BLANK_OPTION;
      const customOption = BLANK_RECORD_OPTION;

      if (selectedStandardValue && !standardOption.label) {
        return {
          standardOption: PROFILE_OTHER_OPTION,
          customOption: { label: capitalize(selectedStandardValue), value: selectedStandardValue },
        };
      }

      return { standardOption, customOption };
    };

    if (!isLoadingProfile) {
      const companySize = COMPANY_SIZE_OPTIONS.find(
        (item) => item.value === profile?.companySize
      ) || { ...BLANK_OPTION };

      const industryValueAndCustomValue = getSelectedOptions(
        ORGANIZATION_INDUSTRY_OPTIONS,
        'organizationIndustry'
      );
      const jobFunctionValueAndCustomValue = getSelectedOptions(
        JOB_FUNCTION_OPTIONS,
        'jobFunction'
      );
      const jobLevelValueAndCustomValue = getSelectedOptions(JOB_LEVEL_OPTIONS, 'jobLevel');
      const jobFunction = JOB_FUNCTION_OPTIONS.find((item) => item.value === profile?.jobFunction);
      const industry = ORGANIZATION_INDUSTRY_OPTIONS.find(
        (item) => item.value === profile?.organizationIndustry
      );
      const subJobFunctionValueAndCustomValue = getSelectedOptions(
        SubJobFunctionList[jobFunction?.label],
        'subJobFunction'
      );
      const subIndustryValueAndCustomValue = getSelectedOptions(
        SubIndustryList[industry?.label],
        'organizationSubIndustry'
      );

      setInitialValues({
        ...INITIAL_STATE,
        company: profile.company
          ? { label: capitalize(profile.company.label), value: profile.company.value }
          : BLANK_RECORD_OPTION,
        name: profile.name || '',
        email: profile.email,
        companySize: companySize,
        customIndustry: industryValueAndCustomValue.customOption,
        customJobFunction: jobFunctionValueAndCustomValue.customOption,
        customJobLevel: jobLevelValueAndCustomValue.customOption,
        customSubIndustry: subIndustryValueAndCustomValue.customOption,
        customSubJobFunction: subJobFunctionValueAndCustomValue.customOption,
        industry: industryValueAndCustomValue.standardOption,
        jobFunction: jobFunctionValueAndCustomValue.standardOption,
        jobLevel: jobLevelValueAndCustomValue.standardOption,
        postalCode: profile.postalCode || '',
        subIndustry: subIndustryValueAndCustomValue.standardOption,
        subJobFunction: subJobFunctionValueAndCustomValue.standardOption,
      });
    }
  }, [
    isLoadingProfile,
    profile,
    profile?.jobFunction,
    profile?.jobLevel,
    profile?.subJobFunction,
    profile?.organizationIndustry,
    profile?.organizationSubIndustry,
    profile?.organizationSize,
    profile?.postalCode,
  ]);

  return (
    <>
      {isLoadingUserSettings ? (
        <Spinner />
      ) : (
        <div>
          <SectionBreakTitle
            className="mb-32"
            titleClassName="text-primary-magenta"
            color={SectionColor.Magenta}
            title="Update profile"
          />
          {currentUser?.is_subscriber && (
            <div className="mb-48 font-suisse-intl text-body-lg font-light">
              Before continuing to settings please update your profile, this will allow us to
              provide articles closer to your interests.
            </div>
          )}
          {READ_ONLY_MODE && (
            <div className="warning">
              <h3>We&apos;re doing site maintenance now.</h3>
              You can view your settings here, but at the moment you can&apos;t save changes. Please
              refresh this page in {READ_ONLY_PHRASE || '10-15 minutes'}; if this message is gone
              then maintenance is complete.
            </div>
          )}
          <UserForm
            ReadOnlyMode={READ_ONLY_MODE}
            initialState={initialValues}
            currentUser={currentUser}
          />
          <LinkSection userSettings={userSettings} />
        </div>
      )}
    </>
  );
};

export default Account;
